import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import CustomPieChart from './PieChart';
import TitleNumberCard from './atoms/TitleNumberCard';
import { formatCurrency } from '../utils/shared';
import FilterTable, { BaseData } from './Table/Table';
import TicketsSummaryTable from './SellersTicketSummary';
import LoadingLottie from './atoms/LoadingLottie';
import { useGenerateAiQueryMutation, useGetEventOrdersReportQuery } from '../store';
import useGetParams from '../hooks/useGetParams';
import AnimatedLoadingText from './atoms/AnimatedLoadingText';
import EventDTO from '../models/event';

interface SellMediumRow extends BaseData {
  title: string;
  count: number | string;
  total: string | JSX.Element;
}

interface ComissionRow extends BaseData {
  title: string;
  total: string | JSX.Element;
}

interface SalesArray {
  title: string;
  count: number | string;
  total: number;
}

interface ComissionArray {
  title: string;
  total: number;
}

const GeneralReportTab = ({ event, hideAiSummary }: { event: EventDTO | null; hideAiSummary?: boolean }) => {
  const { businessId, eventId } = useGetParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const scannedQrs = searchParams.get('scanned-qrs');

  const { data: reportData, isLoading } = useGetEventOrdersReportQuery({
    businessId,
    eventId,
  });
  const [salesArray, setSalesArray] = useState<SalesArray[]>([]);
  const [comissionsArray, setComissionsArray] = useState<ComissionArray[]>([]);
  const ticketsSummaryRef = useRef<HTMLDivElement>(null);

  // Only enable AI summary when not hidden
  const [generateAiQuery, { isLoading: isGeneratingAiQuery, data: aiQueryData }] = hideAiSummary
    ? [() => {}, { isLoading: false, data: null }]
    : useGenerateAiQueryMutation();
  const aiTriggeredForEventRef = useRef<string | null>(null);

  // Add state for highlight effect
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (!reportData) {
      return;
    }
    if (aiTriggeredForEventRef.current === eventId) {
      return;
    }
    aiTriggeredForEventRef.current = eventId;
    const reportDataWithEventDate = {
      ...reportData,
      event_date: event?.date,
      event_end_date: event?.ends_at,
      current_date: new Date().toISOString(),
    };
    generateAiQuery({ contextJson: JSON.stringify(reportDataWithEventDate), businessId });
  }, [reportData, eventId, generateAiQuery, businessId]);

  useEffect(() => {
    if (scannedQrs) {
      ticketsSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsHighlighted(true);
      setTimeout(() => {
        setIsHighlighted(false);
      }, 2000);
    }
  }, [scannedQrs]);

  useEffect(() => {
    if (aiQueryData) {
      console.log(aiQueryData);
    }
  }, [aiQueryData]);

  if (isLoading) {
    return <LoadingLottie />;
  }

  if (!reportData) {
    return <div>No hay datos</div>;
  }

  const salesReportToTotalsTableArray = () => {
    const tableArray: SalesArray[] = [
      {
        title: 'Venta online',
        count: reportData.tickets_count_online,
        total: reportData.total_amount_online,
      },
      {
        title: 'Venta manual',
        count: reportData.tickets_count_manual,
        total: reportData.total_amount_manual,
      },
      {
        title: 'Comisiones',
        count: '-',
        total: reportData.total_comission_amount,
      },
      {
        title: 'Total',
        count: reportData.total_tickets_count,
        total: reportData.net_amount,
      },
    ];
    return tableArray;
  };

  const salesReportToComissionsTableArray = () => {
    const tableArray: ComissionArray[] = [
      {
        title: 'Comisiones de vendedores (Venta manual)',
        total: reportData.sellers_manual_comission_amount,
      },
      {
        title: 'Comisiones de referidos (Venta online)',
        total: reportData.sellers_referral_comission_amount,
      },
      {
        title: 'Comisiones de Avantti (Venta manual)',
        total: reportData.total_manual_service_charge,
      },
      {
        title: 'Service Charge incluido',
        total: reportData.absorbed_service_charge_amount,
      },
      ...(reportData.order_referral_comission_amount > 0 ? [{
        title: 'Comisiones compradores promotores',
        total: reportData.order_referral_comission_amount,
      }] : []),
      {
        title: 'Total comisiones',
        total: reportData.total_comission_amount,
      },
    ];
    return tableArray;
  };

  useEffect(() => {
    if (reportData) {
      setSalesArray(salesReportToTotalsTableArray());
      setComissionsArray(salesReportToComissionsTableArray());
    }
  }, [reportData]);

  const sellMediumRows: SellMediumRow[] = salesArray.map((s) => ({
    id: s.title,
    title: s.title,
    count: s.count,
    total: <div className='whitespace-nowrap'>{formatCurrency(s.total)}</div>,
  }));

  const sellMediumConfig = [
    {
      id: 'title',
      label: 'Canal de venta',
    },
    {
      id: 'count',
      label: 'Cantidad',
    },
    {
      id: 'total',
      label: 'Total',
    },
  ];

  const comissionsTableRows: ComissionRow[] = comissionsArray.map((c) => ({
    id: c.title,
    title: c.title,
    total: <div className='whitespace-nowrap'>{formatCurrency(c.total)}</div>,
  }));

  const comissionsTableConfig = [
    {
      id: 'title',
      label: 'Comisión',
    },
    {
      id: 'total',
      label: 'Total',
    },
  ];

  const dataMedium = [
    { name: 'Venta online', value: reportData.sales_channel_share.online },
    { name: 'Venta manual', value: reportData.sales_channel_share.seller },
    { name: 'Venta referidos', value: reportData.sales_channel_share.referral },
  ];

  const dataComission = [
    { name: 'Vendedores manual', value: reportData.comissions_share.seller },
    { name: 'Vendedores referidos', value: reportData.comissions_share.referral },
    { name: 'Avantti.io', value: reportData.comissions_share.avantti },
    ...(reportData.order_referral_comission_amount > 0 ? [{
      name: 'Compradores promotores', value: reportData.comissions_share.order_referral,
    }] : []),
    ...(reportData.absorbed_service_charge_amount > 0 ? [{
      name: 'Service Charge incluido', value: reportData.comissions_share.absorbed_service_charge,
    }] : []),
  ];

  return (
    <>
      {!hideAiSummary && (
        <AnimatedLoadingText isLoading={isGeneratingAiQuery} loadingText="Generando reporte con IA..." text={aiQueryData?.data?.output_text} title='Resumen IA (BETA)' />
      )}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full glass-surface'>
        <div className='w-full'>
          <TitleNumberCard
            title="Ingresos totales brutos"
            number={formatCurrency(reportData.total_amount)}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Total comisiones"
            number={formatCurrency(reportData.total_comission_amount)}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Ingresos netos"
            number={formatCurrency(reportData.net_amount)}
          />
        </div>
        <div className='w-full'>
          <TitleNumberCard
            title="Monto total descuentos"
            number={formatCurrency(reportData.total_discounts)}
          />
        </div>
      </div>
      <div className='flex flex-col gap-4 glass-surface'>
        <h1>Medios de venta</h1>
        <FilterTable
          rows={sellMediumRows}
          headCells={sellMediumConfig}
          pagination={{ enabled: false }}
          searchEnabled={false}
          tableId="sell-medium-table"
        />
      </div>
      <div className='flex flex-col gap-4 glass-surface'>
        <h1>Desglose de comisiones</h1>
        <FilterTable
          rows={comissionsTableRows}
          headCells={comissionsTableConfig}
          pagination={{ enabled: false }}
          searchEnabled={false}
          tableId="comissions-table"
        />
      </div>
      <div
        className='flex flex-col gap-4 transition-all duration-300 glass-surface'
        ref={ticketsSummaryRef}
      >
        <h1 className={`${isHighlighted ? 'bg-primary-100' : ''}`}>Resumen de ventas por tipo de entrada</h1>
        <TicketsSummaryTable summaries={reportData.ticket_summary} />
      </div>
      <div className='flex flex-col lg:flex-row gap-4 glass-surface'>
        <div className='w-full glass-surface'>
          <h1 className='text-center'>Porcentaje de monto por medio de venta</h1>
          <CustomPieChart data={dataMedium} />
        </div>
        {reportData.total_comission_amount > 0 && (
          <div className='w-full glass-surface'>
            <h1 className='text-center'>Porcentaje de comisiones</h1>
            <CustomPieChart data={dataComission} />
          </div>
        )}
      </div>
    </>
  );
};

export default GeneralReportTab;
